import React, { useState } from 'react';
import { X, Plus, Trash2, Upload, Wand2, Star, Loader2, RefreshCw, Lightbulb, Eye } from 'lucide-react';
import { 
  generateBlogContent, 
  generateBlogIdeas, 
  parseGeneratedContent, 
  generateHTMLFromContent,
  applyGeneratedPost
} from '../services/kimiAIService';

const AutoDesignModal = ({ theme, onGenerate, onClose }) => {
  const [mode, setMode] = useState('manual'); // 'manual' or 'ai'
  const [formData, setFormData] = useState({
    title: '',
    mainImage: null,
    mainImagePreview: null,
    sections: [
      { heading: '', content: '', image: null, imagePreview: null },
      { heading: '', content: '', image: null, imagePreview: null },
      { heading: '', content: '', image: null, imagePreview: null }
    ]
  });

  // AI Generator State
  const [aiState, setAiState] = useState({
    topic: '',
    category: 'General',
    generatedContent: null,
    isGenerating: false,
    error: null,
    suggestions: []
  });

  const categories = [
    'General', 'Automotive', 'Industrial', 'Medical', 'Telecommunications',
    'Manufacturing', 'Guide', 'Technical', 'Compliance', 'Maintenance'
  ];

  // Handle title change
  const handleTitleChange = (value) => {
    setFormData(prev => ({ ...prev, title: value }));
  };

  // Handle main image upload
  const handleMainImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          mainImage: file,
          mainImagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle section changes
  const handleSectionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  // Handle section image upload
  const handleSectionImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          sections: prev.sections.map((section, i) =>
            i === index ? { ...section, image: file, imagePreview: reader.result } : section
          )
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Add new section
  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, { heading: '', content: '', image: null, imagePreview: null }]
    }));
  };

  // Remove section
  const removeSection = (index) => {
    if (formData.sections.length > 1) {
      setFormData(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== index)
      }));
    } else {
      alert('You must have at least one section');
    }
  };

  // Generate blog post from manual form
  const handleManualGenerate = () => {
    if (!formData.title.trim()) {
      alert('Please enter a title for your blog post');
      return;
    }

    const hasContent = formData.sections.some(s => s.heading.trim() || s.content.trim());
    if (!hasContent) {
      alert('Please add at least one section with heading or content');
      return;
    }

    // Convert manual form to structured format
    const structured = {
      title: formData.title,
      metaDescription: '',
      sections: formData.sections
        .filter(s => s.heading.trim() || s.content.trim())
        .map(s => ({
          heading: s.heading,
          paragraphs: s.content.split('\n').filter(l => l.trim()).map(l => ({
            type: l.trim().startsWith('-') ? 'list' : 'paragraph',
            content: l.trim().replace(/^[-•*]\s*/, '')
          }))
        }))
    };

    const html = generateHTMLFromContent(structured, formData.mainImagePreview);
    
    onGenerate({
      title: formData.title,
      content: html,
      author: 'Qingshun Team',
      category: aiState.category || 'General'
    });
  };

  // AI Content Generation Functions
  const generateIdeas = async () => {
    setAiState(prev => ({ ...prev, isGenerating: true, error: null }));
    
    try {
      const result = await generateBlogIdeas(aiState.category, 5);
      
      if (result.success) {
        const ideas = result.content.split('\n').filter(line => line.trim());
        setAiState(prev => ({ 
          ...prev, 
          suggestions: ideas,
          isGenerating: false 
        }));
      } else {
        setAiState(prev => ({ 
          ...prev, 
          error: result.error || 'Failed to generate ideas',
          isGenerating: false 
        }));
      }
    } catch (error) {
      setAiState(prev => ({ 
        ...prev, 
        error: error.message,
        isGenerating: false 
      }));
    }
  };

  const generateFullContent = async () => {
    if (!aiState.topic.trim()) {
      setAiState(prev => ({ ...prev, error: 'Please enter a topic for your blog post' }));
      return;
    }

    setAiState(prev => ({ ...prev, isGenerating: true, error: null, generatedContent: null }));

    try {
      const result = await generateBlogContent(aiState.topic, aiState.category);
      
      if (result.success) {
        const structured = parseGeneratedContent(result.content);
        setAiState(prev => ({ 
          ...prev, 
          generatedContent: structured,
          isGenerating: false 
        }));
      } else {
        setAiState(prev => ({ 
          ...prev, 
          error: result.error || 'Failed to generate content',
          isGenerating: false 
        }));
      }
    } catch (error) {
      setAiState(prev => ({ 
        ...prev, 
        error: error.message,
        isGenerating: false 
      }));
    }
  };

  const applyGeneratedContent = () => {
    if (!aiState.generatedContent) return;

    const { title, content, metaDescription } = applyGeneratedPost(
      aiState.generatedContent, 
      formData.mainImagePreview
    );

    // Apply to main form
    setFormData(prev => ({
      ...prev,
      title: title,
      content: content,
      author: 'Qingshun Team',
      category: aiState.category,
      seoTitle: title,
      seoDescription: metaDescription
    }));

    // Apply to main form and close modal
    onGenerate({
      title: title,
      content: content,
      author: 'Qingshun Team',
      category: aiState.category
    });
  };

  // Preview generated content in new window
  const previewGeneratedContent = () => {
    if (!aiState.generatedContent) return;

    const { content } = applyGeneratedPost(
      aiState.generatedContent, 
      formData.mainImagePreview
    );

    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Preview: ${aiState.generatedContent.title || 'Generated Post'}</title>
        <style>
          body { font-family: system-ui, -apple-system, sans-serif; max-width: 900px; margin: 0 auto; padding: 40px 20px; background: #f5f5f5; }
          .container { background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
          img { max-width: 100%; height: auto; border-radius: 12px; margin: 20px 0; }
          h1 { color: #1a1a1a; font-size: 2.5rem; }
          h2 { color: #dc2626; font-size: 1.75rem; margin-top: 2rem; border-bottom: 2px solid #fee2e2; padding-bottom: 0.5rem; }
          ul { list-style: none; padding: 0; }
          li { background: #f9fafb; margin: 1rem 0; padding: 1rem; border-radius: 8px; border-left: 4px solid #dc2626; line-height: 1.6; }
          strong { color: #dc2626; }
          a { color: #dc2626; }
        </style>
      </head>
      <body>
        <div class="container">
          ${content}
        </div>
      </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const selectSuggestion = (suggestion) => {
    // Try to extract the topic from the suggestion
    const match = suggestion.match(/^\d+[\.\)]\s*[*`]?(.+?)[*`]?\s*[-:]/);
    if (match) {
      setAiState(prev => ({ ...prev, topic: match[1].trim(), suggestions: [] }));
    } else {
      setAiState(prev => ({ ...prev, topic: suggestion.replace(/^\d+[\.\)]\s*/, '').split('\n')[0].trim(), suggestions: [] }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
      <div 
        className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
          theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold">✨ Auto Design Your Blog Post</h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Mode Tabs */}
        <div className={`flex border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setMode('ai')}
            className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
              mode === 'ai'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Star className="w-5 h-5" />
            🤖 AI Auto Generate
          </button>
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
              mode === 'manual'
                ? 'bg-gradient-to-r from-red-600 to-red-700 text-white'
                : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Wand2 className="w-5 h-5" />
            ✏️ Manual Create
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {mode === 'ai' ? (
            /* AI Generator Mode */
            <div className="space-y-6">
              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  <Lightbulb className="w-4 h-4 inline mr-1" />
                  Blog Topic / Title *
                </label>
                <input
                  type="text"
                  value={aiState.topic}
                  onChange={(e) => setAiState(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="e.g., The Future of EV Connectors, Cost-Saving Strategies for Manufacturers"
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-purple-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-purple-500'
                  } focus:outline-none`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Be specific for better results. Try "5 Key Trends in Automotive Connectors 2025"
                </p>
              </div>

              {/* Category Select */}
              <div>
                <label className="block text-sm font-semibold mb-2">Category *</label>
                <select
                  value={aiState.category}
                  onChange={(e) => setAiState(prev => ({ ...prev, category: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-700 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:border-purple-500`}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Error Message */}
              {aiState.error && (
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                }`}>
                  ⚠️ {aiState.error}
                </div>
              )}

              {/* Suggestions */}
              {aiState.suggestions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">💡 Suggested Topics</h3>
                  <div className="space-y-2">
                    {aiState.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectSuggestion(suggestion)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          theme === 'dark' 
                            ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated Content Preview */}
              {aiState.generatedContent && (
                <div className={`p-4 rounded-lg border-2 ${
                  theme === 'dark' ? 'bg-gray-800 border-purple-500' : 'bg-gray-50 border-purple-400'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Star className="w-4 h-4 text-purple-500" />
                      Generated Content Preview
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={previewGeneratedContent}
                        className="text-sm text-blue-500 hover:text-blue-400 flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Preview
                      </button>
                      <button
                        onClick={generateFullContent}
                        className="text-sm text-purple-500 hover:text-purple-400 flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Regenerate
                      </button>
                    </div>
                  </div>
                  <h4 className="font-bold text-lg mb-2">{aiState.generatedContent.title}</h4>
                  {aiState.generatedContent.metaDescription && (
                    <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {aiState.generatedContent.metaDescription.substring(0, 150)}...
                    </p>
                  )}
                  {aiState.generatedContent.sections.map((section, idx) => (
                    <div key={idx} className="mb-3 p-3 rounded-lg bg-black/10 dark:bg-white/5">
                      <h5 className="font-semibold text-sm text-purple-500 flex items-center gap-2">
                        {idx + 1}. {section.heading}
                      </h5>
                      <div className="mt-1 text-xs text-gray-500">
                        {section.paragraphs?.length || 0} items
                        {section.isList && ' • List format'}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 mt-2">
                    {aiState.generatedContent.sections.length} sections generated
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={generateIdeas}
                  disabled={aiState.isGenerating}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 hover:bg-gray-700 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  } disabled:opacity-50`}
                >
                  {aiState.isGenerating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Lightbulb className="w-5 h-5" />
                  )}
                  Get Topic Ideas
                </button>
                <button
                  onClick={generateFullContent}
                  disabled={aiState.isGenerating || !aiState.topic.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 hover:from-purple-700 hover:via-pink-700 hover:to-red-700 transition-all shadow-lg disabled:opacity-50"
                >
                  {aiState.isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Star className="w-5 h-5" />
                      Generate Full Post
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Manual Mode */
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold mb-2">Blog Post Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Enter your blog post title"
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-red-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-red-500'
                  } focus:outline-none`}
                />
              </div>

              {/* Main Image */}
              <div>
                <label className="block text-sm font-semibold mb-2">Main Featured Image (Optional)</label>
                <div className="flex items-center gap-4">
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 hover:bg-gray-700'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}>
                    <Upload className="w-5 h-5" />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainImageUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.mainImagePreview && (
                    <img
                      src={formData.mainImagePreview}
                      alt="Main preview"
                      className="h-20 w-20 object-cover rounded-lg"
                    />
                  )}
                </div>
              </div>

              {/* Sections */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold">Content Sections</label>
                  <button
                    onClick={addSection}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Section
                  </button>
                </div>

                <div className="space-y-6">
                  {formData.sections.map((section, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold">Section {index + 1}</h3>
                        {formData.sections.length > 1 && (
                          <button
                            onClick={() => removeSection(index)}
                            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Section Heading */}
                      <div className="mb-3">
                        <input
                          type="text"
                          value={section.heading}
                          onChange={(e) => handleSectionChange(index, 'heading', e.target.value)}
                          placeholder="Section heading (e.g., 'Key Features')"
                          className={`w-full px-3 py-2 rounded border transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:border-red-500`}
                        />
                      </div>

                      {/* Section Content */}
                      <div className="mb-3">
                        <textarea
                          value={section.content}
                          onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                          placeholder="Section content. Use new lines for bullet points.
Example:
- Superior quality and durability
- Advanced manufacturing processes
- Certified to international standards"
                          rows="5"
                          className={`w-full px-3 py-2 rounded border transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:border-red-500`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Tip: Start lines with "-" or "•" for bullet points. Use ":" for bold labels.
                        </p>
                      </div>

                      {/* Section Image */}
                      <div className="flex items-center gap-4">
                        <label className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${
                          theme === 'dark'
                            ? 'bg-gray-900 hover:bg-gray-700'
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}>
                          <Upload className="w-4 h-4" />
                          <span>Add Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleSectionImageUpload(index, e)}
                            className="hidden"
                          />
                        </label>
                        {section.imagePreview && (
                          <img
                            src={section.imagePreview}
                            alt={`Section ${index + 1} preview`}
                            className="h-16 w-16 object-cover rounded"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex justify-end gap-4 p-6 border-t ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              theme === 'dark'
                ? 'bg-gray-800 hover:bg-gray-700'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          {mode === 'ai' ? (
            <button
              onClick={applyGeneratedContent}
              disabled={!aiState.generatedContent}
              className="px-6 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 hover:from-purple-700 hover:via-pink-700 hover:to-red-700 transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
            >
              <Star className="w-5 h-5" />
              Apply Generated Content
            </button>
          ) : (
            <button
              onClick={handleManualGenerate}
              className="px-6 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-all shadow-lg flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate Beautiful Post
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoDesignModal;
